import { TouchCopyable } from '@euler/app/components/TouchCopyable';
import { Typography } from '@euler/app/components/typography';
import { Colors } from '@euler/components/Colors';
import { Spinner } from '@euler/components/Spinner';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useAudioPlaybackInSilentMode } from '@euler/functions/useAudioPlaybackInSilentMode';
import { Audio } from 'expo-av';
import { Pause, Play } from 'lucide-react-native';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { StyleProp, TouchableOpacity, ViewStyle } from 'react-native';
import { useTheme } from 'styled-components';
import { OrderInfoSection } from '../OrderInfoSection';

export const RemarkSection = memo(
  ({
    remark,
    voiceUrl,
    autoPlay,
    style,
  }: {
    remark?: string | null;
    voiceUrl?: string | null;
    /**
     * When `true` and a `voiceUrl` is provided, the audio is played
     * automatically once the URL becomes available. The user can pause via
     * the in-section button. Re-triggers when the URL changes.
     */
    autoPlay?: boolean;
    style?: StyleProp<ViewStyle>;
  }) => {
    const { rpx } = useResponsiveContext();
    const hasText = Boolean(remark?.trim());
    const hasVoice = Boolean(voiceUrl);
    if (!hasText && !hasVoice) return null;
    return (
      <OrderInfoSection
        title="服务备注"
        style={style}
        headerRight={
          voiceUrl ? (
            <RemarkVoicePlayButton url={voiceUrl} autoPlay={autoPlay} />
          ) : undefined
        }
      >
        {hasText ? (
          <TouchCopyable text={remark!}>
            <Typography.Text regular size={rpx(13)} color={Colors.Gray3}>
              {remark}
            </Typography.Text>
          </TouchCopyable>
        ) : null}
      </OrderInfoSection>
    );
  },
);

const RemarkVoicePlayButton = memo(
  ({ url, autoPlay }: { url: string; autoPlay?: boolean }) => {
    const theme = useTheme();
    const { rpx } = useResponsiveContext();
    const [isPlaying, setIsPlaying] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const soundRef = useRef<Audio.Sound | null>(null);
    const autoPlayedForRef = useRef<string | null>(null);

    // Override the iOS silent switch while audio is actively playing
    // (auto-played or user-tapped — once it's playing, the user's intent
    // is to hear it). Auto-reverts via the hook's cleanup when playback
    // pauses, finishes, or the component unmounts.
    useAudioPlaybackInSilentMode(isPlaying);

    // Unload on unmount or when the URL changes.
    useEffect(() => {
      return () => {
        const s = soundRef.current;
        soundRef.current = null;
        setIsPlaying(false);
        void s?.unloadAsync();
      };
    }, [url]);

    const togglePlay = useCallback(async () => {
      if (isLoading) return;
      try {
        if (soundRef.current) {
          const status = await soundRef.current.getStatusAsync();
          if (status.isLoaded && status.isPlaying) {
            await soundRef.current.pauseAsync();
            setIsPlaying(false);
          } else {
            await soundRef.current.playAsync();
            setIsPlaying(true);
          }
          return;
        }
        setIsLoading(true);
        const { sound } = await Audio.Sound.createAsync(
          { uri: url },
          { shouldPlay: true },
        );
        soundRef.current = sound;
        setIsPlaying(true);
        sound.setOnPlaybackStatusUpdate(status => {
          if (!status.isLoaded) return;
          if (status.didJustFinish) {
            setIsPlaying(false);
            // Rewind so the next tap replays from the start.
            void sound.setPositionAsync(0);
          }
        });
      } catch (e) {
        console.warn('failed to play remark voice:', e);
        setIsPlaying(false);
      } finally {
        setIsLoading(false);
      }
    }, [url, isLoading]);

    // Auto-play once per URL when the caller asks for it. The user can
    // tap the button at any time to pause — same controls as manual play.
    useEffect(() => {
      if (!autoPlay) return;
      if (autoPlayedForRef.current === url) return;
      autoPlayedForRef.current = url;
      void togglePlay();
      // togglePlay is intentionally omitted from deps to keep this firing
      // exactly once per URL even if memoized identity changes.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoPlay, url]);

    const size = rpx(26);
    const iconSize = rpx(11);
    return (
      <TouchableOpacity
        activeOpacity={0.7}
        onPress={togglePlay}
        hitSlop={{ top: 8, right: 8, bottom: 8, left: 8 }}
        style={{
          width: size,
          height: size,
          borderRadius: size / 2,
          backgroundColor: theme.link,
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {isLoading ? (
          <Spinner size={iconSize} color="#fff" />
        ) : isPlaying ? (
          <Pause color="#fff" size={iconSize} fill="#fff" strokeWidth={0} />
        ) : (
          <Play color="#fff" size={iconSize} fill="#fff" strokeWidth={0} />
        )}
      </TouchableOpacity>
    );
  },
);
RemarkVoicePlayButton.displayName = 'RemarkVoicePlayButton';
